<!--
  THE DOCUMENTS TAB — the house pattern for a settings tab that holds a LIST OF N ITEMS.

  `consent-form-settings.component.html:16-40` is the analogue and it is the opposite of one panel
  per item: FIXED, NAMED SECTIONS THAT CONTAIN LISTS. The add action is an `expansion-header-icon`
  in the section header, and each item is a `note-type-section` whose `action-heading` carries its
  own icon buttons.

  That shape matters for a reason a screenshot makes obvious: with zero documents, one-panel-per-
  document renders NOTHING, so the tab reads as a bare title and a stray button. A fixed section
  still shows its header and an empty body, so the page has structure before it has data.

  THE TWO SECTIONS ARE `requiresResponse` (§14.2), which is the same split consent uses for
  onboarding versus procedure: they are genuinely different things to a practice. One is a document
  they are waiting on; the other is a file they hand out. The add icon in each header opens the
  upload dialog PRE-SET to that section, so the flag is chosen by where the user clicked rather than
  by a control they have to find.

  Configuring happens in a dialog, not in this template: a settings tab is a list of what is
  configured, and a change is a decision you take and come back from.
-->
<div class="practice-document-settings-container">
    <div class="settings-header">
        <h2>DOCUMENT REPOSITORY</h2>
        <p class="hint">Upload your practice's existing PDF forms and configure how they are sent
            and completed. Choose whether forms are dispatched automatically with appointment
            invites or sent manually by staff. Once submitted, completed copies are saved directly
            to the patient's file and, if required, routed to your Inbox for review.</p>

        <!--
          The format note is a list, not prose: the two PDF kinds behave differently for the
          patient, and a practice needs to be able to scan for the one it is holding. Left-aligned
          against the centred `.settings-header` because centred bullets do not read as a list.
        -->
        <div class="hint pds-format-note">
            <p>Note on how MedicAI processes different PDF formats:</p>
            <ul>
                <li><strong>Interactive (Acroform) PDFs:</strong> Embedded directly into the web
                    page so patients can complete and submit them on-screen.</li>
                <li><strong>Standard (Flat) PDFs:</strong> Patients must download the document,
                    complete it offline, and upload it back.</li>
            </ul>
        </div>
    </div>

    <div class="pds-alert pds-alert--warn" *ngIf="listError" data-test="list-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ listError }}</div>
    </div>

    <div class="pds-loading" *ngIf="isLoading" data-test="list-loading">
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    </div>

    <!-- `multi="false"`, as `note-type-settings` and `consent-form-settings` both are. -->
    <mat-accordion multi="false">

        <!-- ── §14.2: documents the practice is waiting on ────────────────────────────────── -->
        <mat-expansion-panel [expanded]="true" data-test="section-responding">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    DOCUMENTS THAT NEED A RESPONSE
                    <mat-icon class="expansion-header-icon" data-test="add-response-document"
                        (click)="openUploadDialog($event, true)" color="primary"
                        matTooltip="Upload a document patients have to send back">add</mat-icon>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <mat-accordion class="pds-documents" multi="true">
                <mat-expansion-panel class="pds-document" data-test="document-row"
                    *ngFor="let doc of respondingRows; trackBy: trackById" (opened)="onDocumentOpened(doc)">
                    <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                        <ng-container *ngTemplateOutlet="documentHeader; context: { $implicit: doc }">
                        </ng-container>
                    </mat-expansion-panel-header>

                    <ng-container *ngTemplateOutlet="documentRow; context: { $implicit: doc }">
                    </ng-container>
                </mat-expansion-panel>
            </mat-accordion>

            <!--
              A FAILED LOAD IS NOT AN EMPTY LIST — per section, since there are now two of them.
              Both go through the same getter, so the exclusivity cannot hold in one and not the
              other. See the component's header.
            -->
            <p class="hint" *ngIf="showRespondingEmptyState" data-test="empty-state">
                No documents that need a response yet. Upload a PDF and it will appear here.
            </p>
        </mat-expansion-panel>

        <!-- ── §14.2: information only — attached, never asked back ───────────────────────── -->
        <mat-expansion-panel [expanded]="false" data-test="section-information">
            <mat-expansion-panel-header>
                <mat-panel-title>
                    INFORMATION SHEETS
                    <mat-icon class="expansion-header-icon" data-test="add-information-document"
                        (click)="openUploadDialog($event, false)" color="primary"
                        matTooltip="Upload a sheet patients are sent and never asked to return">
                        add</mat-icon>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <mat-accordion class="pds-documents" multi="true">
                <mat-expansion-panel class="pds-document" data-test="document-row"
                    *ngFor="let doc of informationRows; trackBy: trackById" (opened)="onDocumentOpened(doc)">
                    <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
                        <ng-container *ngTemplateOutlet="documentHeader; context: { $implicit: doc }">
                        </ng-container>
                    </mat-expansion-panel-header>

                    <ng-container *ngTemplateOutlet="documentRow; context: { $implicit: doc }">
                    </ng-container>
                </mat-expansion-panel>
            </mat-accordion>

            <p class="hint" *ngIf="showInformationEmptyState" data-test="empty-state">
                No information sheets yet. Upload a PDF and it will appear here.
            </p>
        </mat-expansion-panel>
    </mat-accordion>

    <div class="mobile-spacer"></div>
</div>

<!--
  ONE ROW, used by both sections, in two halves.

  Templates rather than four copies: the row is the same document in either section, and two copies
  of "what happens to the patient who receives this" is two chances for the sections to disagree
  about it — the reason `practice-document-copy.ts` exists at all.
-->

<!--
  THE COLLAPSED ROW — what fifty documents look like when none of them is open.

  A practice can have fifty of these, and every one of them was drawing its metadata, its delivery
  routes and its whole appointment-triggers block whether anyone was reading it or not. So the row
  is a panel now, and this is its header: the name, and THE SENTENCE THIS SCREEN IS FOR.

  `fillMode` is still never printed. What is printed is what happens to the patient who receives
  this document — which is the one thing worth carrying into a collapsed row, because it is the
  only question a practice has about a document they cannot see the rest of.

  The three buttons stop propagation because a `mat-expansion-panel-header` toggles on any click
  inside it: without it, deleting a document would also collapse it on the way out, and opening the
  blank would close the row you opened it from.
-->
<ng-template #documentHeader let-doc>
    <mat-panel-title class="pds-header-title">
        <span class="pds-row-name">{{ doc.name }}</span>
        <span class="pds-inactive" *ngIf="!doc.active">(inactive)</span>
    </mat-panel-title>

    <mat-panel-description class="pds-header-description">
        <span class="pds-fill-mode">
            <mat-icon>{{ patientOutcomeIcon(doc) }}</mat-icon>
            <span data-test="patient-outcome">{{ patientOutcomeSentence(doc) }}</span>
        </span>

        <span class="pds-header-actions">
            <button mat-icon-button type="button" data-test="edit-document" color="primary"
                (click)="openEditDialog($event, doc)" matTooltip="Edit this document">
                <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button type="button" data-test="open-blank" (click)="openBlank($event, doc)"
                matTooltip="Open the blank">
                <mat-icon>open_in_new</mat-icon>
            </button>
            <button mat-icon-button type="button" data-test="delete-document" color="warn"
                (click)="deleteDocument($event, doc)" matTooltip="Delete this document">
                <mat-icon>delete</mat-icon>
            </button>
        </span>
    </mat-panel-description>
</ng-template>

<!--
  THE BODY — everything that is only worth drawing for the one document being read.

  Split from the header rather than hidden with a `*ngIf`: an expansion panel does not instantiate
  its body until it is first opened, so a collapsed practice pays nothing for the fifty documents
  they are not looking at. That is also why the triggers read is lazy — see `onDocumentOpened`.
-->
<ng-template #documentRow let-doc>
    <p class="hint" data-test="download-only-reason" *ngIf="downloadOnlyReason(doc)">
        {{ downloadOnlyReason(doc) }}
    </p>

    <p class="hint pds-description" *ngIf="doc.description" data-test="document-description">
        {{ doc.description }}
    </p>

    <!--
      THE METADATA, AS ONE CHIP STRIP RATHER THAN SEVEN LABELLED LINES.

      Every other tab in this folder gives an item a heading, its icon buttons, and then ONE body.
      Seven `<strong>Label:</strong> value` paragraphs per document was this tab's own invention and
      it pushed the delivery routes — the only part of the row a practice can act on — off the
      screen. `documentFacts` builds each chip as a COMPLETE fact, because a chip has no room for a
      caption beside its value; the caption it would have had is the tooltip.

      Read-only, so `mat-chip-set` and not the `mat-chip-grid` the two editable lists below use.
      The grid exists to host an input and to make its chips focusable as form controls, and a set
      of facts is neither.
    -->
    <mat-chip-set class="pds-meta" aria-label="Document details">
        <mat-chip *ngFor="let fact of documentFacts(doc)" [matTooltip]="fact.tooltip" [disableRipple]="true"
            data-test="document-fact">
            <mat-icon matChipAvatar>{{ fact.icon }}</mat-icon>
            {{ fact.label }}
        </mat-chip>
    </mat-chip-set>

    <mat-form-field class="full-width" appearance="outline" *ngIf="deliveryRoutes(doc).length > 0">
        <mat-label>Delivery routes</mat-label>
        <mat-chip-grid #deliveryChipGrid>
            <mat-chip-row *ngFor="let route of deliveryRoutes(doc)" [editable]="false" data-test="delivery-route">
                {{ route }}
            </mat-chip-row>
        </mat-chip-grid>
        <input [matChipInputFor]="deliveryChipGrid" disabled />
        <mat-hint>Edit this document to change how patients are asked for it.</mat-hint>
    </mat-form-field>

    <!--
      WHAT ACTUALLY REACHES A PATIENT — the flags above AND the triggers below, in one sentence.

      There are three of them and the component picks: nothing sends this at all, only a trigger
      does, or the only triggers there are have been paused. It used to be one sentence read off the
      flags alone, which §24.5 made untrue the moment a trigger could send a document whose flags
      were all off — and it said so directly above the chips that proved otherwise.

      Nothing is said while the trigger read is outstanding or has failed. See the component.
    -->
    <p class="hint" *ngIf="deliveryReachSentence(doc) as reach" data-test="delivery-reach">
        {{ reach }}
    </p>

    <!--
      APPOINTMENT TRIGGERS — §3.2, §24.2, §24.5.

      The fourth delivery route, and the only one that is a LIST rather than a boolean. The ANSWER
      is drawn in the row, in the same `mat-chip-grid` inside a `mat-form-field` that the delivery
      routes above use, because a practice reading a row is asking ONE question — what happens to
      this document — and an answer split between a panel and a dialog is two answers.

      COMPOSING a new rule is not part of that answer, and it is the one thing here that left: the
      four controls that were drawn under every document in both sections now live in
      `practice-document-trigger-dialog`, which the add icon opens. What stayed is everything that
      describes the rules that already exist.

      Each chip carries its own two actions, and they are deliberately not the same button: PAUSE
      is a POST with `active: false` and keeps the trigger; REMOVE is a hard DELETE and does not.
      §3.2 names that confusion as the one this UI has to avoid, so the tooltips, the confirmation
      and the chip's own "(paused)" all say which is which.
    -->
    <div class="pds-triggers" data-test="appointment-triggers" *ngIf="!stateMode">
        <div class="action-heading">
            <h4 class="pds-triggers-title">Appointment triggers</h4>
            <button mat-icon-button type="button" color="primary" data-test="add-trigger" (click)="startAddingLink(doc)"
                matTooltip="Add a trigger that sends this document for an appointment type">
                <mat-icon>add</mat-icon>
            </button>
        </div>

        <div class="pds-loading" *ngIf="isLoadingLinks(doc)" data-test="links-loading">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>

        <div class="pds-alert pds-alert--warn" *ngIf="linkLoadError(doc)">
            <mat-icon>error_outline</mat-icon>
            <div data-test="links-load-error">{{ linkLoadError(doc) }}</div>
        </div>

        <mat-form-field class="full-width" appearance="outline" *ngIf="linksFor(doc).length > 0">
            <mat-label>Sent for these appointment types</mat-label>
            <mat-chip-grid #triggerChipGrid>
                <mat-chip-row *ngFor="let link of linksFor(doc); trackBy: trackById" [editable]="false"
                    data-test="appointment-trigger" class="pds-trigger-chip"
                    [class.pds-trigger--paused]="link.active === false">
                    <span data-test="trigger-label">{{ linkChipLabel(link) }}</span>
                    <button mat-icon-button type="button" class="pds-trigger-action" data-test="pause-trigger"
                        [matTooltip]="pauseTooltip(link)" [attr.aria-label]="pauseTooltip(link)"
                        (click)="toggleLinkPaused(doc, link)">
                        <mat-icon>{{ pauseIcon(link) }}</mat-icon>
                    </button>
                    <button mat-icon-button type="button" color="warn" class="pds-trigger-action"
                        data-test="remove-trigger" [matTooltip]="removeLinkTooltip"
                        [attr.aria-label]="removeLinkTooltip" (click)="removeLink(doc, link)">
                        <mat-icon>delete_outline</mat-icon>
                    </button>
                </mat-chip-row>
            </mat-chip-grid>
            <input [matChipInputFor]="triggerChipGrid" disabled />
            <mat-hint>Pausing keeps a trigger and stops it sending. Removing deletes it.</mat-hint>
        </mat-form-field>

        <!--
          A FAILED LOAD IS NOT AN EMPTY LIST — the §17 defect, one level down and with a worse
          claim. `showNoLinks` is the single getter that decides, exactly as `sectionEmptyState` is
          for the document list.
        -->
        <p class="hint" *ngIf="showNoLinks(doc)" data-test="no-appointment-triggers">
            {{ noLinksSentence }}
        </p>

        <div class="pds-alert pds-alert--warn" *ngIf="linkErrorFor(doc)">
            <mat-icon>error_outline</mat-icon>
            <div data-test="link-error">{{ linkErrorFor(doc) }}</div>
        </div>

    </div>
</ng-template>